import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Company } from 'store/authSlice'; // Import your Company type

interface RoleProtectedRouteProps {
  allowedRoles: string[];
  children: ReactNode;
}

interface UserData {
  isLoggedIn: boolean;
  role: string;
  email: string;
  name: string;
  companyId: string | null;
  companies?: Company[];
  loginTime?: number;
  expiresAt?: number;
  // Add fields that are actually stored in sessionStorage
  id?: number;
  departmentId?: string | null;
  company_name?: string;
  department_name?: string;
  role_id?: number;
}

const RoleProtectedRoute = ({
  allowedRoles,
  children
}: RoleProtectedRouteProps) => {
  const [authStatus, setAuthStatus] = useState<
    'checking' | 'authenticated' | 'unauthenticated' | 'unauthorized'
  >('checking');

  useEffect(() => {
    const validateAccess = () => {
      try {
        const userData = sessionStorage.getItem('user');

        // No user data found
        if (!userData) {
          setAuthStatus('unauthenticated');
          return;
        }

        const user: UserData = JSON.parse(userData);

        // Basic validation of user object structure
        if (!user || typeof user !== 'object' || !user.isLoggedIn) {
          sessionStorage.removeItem('user');
          setAuthStatus('unauthenticated');
          return;
        }

        // ✅ FIXED: Allow companyId to be null (for super admin)
        // Validate required fields
        if (!user.role || !user.email) {
          sessionStorage.removeItem('user');
          setAuthStatus('unauthenticated');
          return;
        }

        // Check session expiration if available
        if (user.expiresAt && Date.now() > user.expiresAt) {
          sessionStorage.removeItem('user');
          setAuthStatus('unauthenticated');
          return;
        }

        // Check role authorization
        if (!allowedRoles.includes(user.role)) {
          setAuthStatus('unauthorized');
          return;
        }

        setAuthStatus('authenticated');
      } catch (error) {
        // Handle JSON parse errors or other issues
        sessionStorage.removeItem('user');
        setAuthStatus('unauthenticated');
      }
    };

    validateAccess();
  }, [allowedRoles]);

  // Show loading while checking authentication
  if (authStatus === 'checking') {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: '100vh' }}
      >
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Checking access...</span>
        </div>
      </div>
    );
  }

  // Redirect based on auth status
  if (authStatus === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  if (authStatus === 'unauthorized') {
    return <Navigate to="/errors/403" replace />;
  }

  return <>{children}</>;
};

export default RoleProtectedRoute;
